import { useState, useEffect, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { createPortal } from "react-dom";
import { PageContainer } from "@/app/layouts/PageContainer";
import { PageHeader } from "@/components/page-header";
import { FieldList, type FieldListItem } from "@/components/field-list";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export interface DetailTab {
  id: string;
  label: string;
  content: ReactNode;
  /** If true, content remains mounted when not active. Useful for tabs owning form state. */
  forceMount?: boolean;
}

export type DetailPageHeaderVariant = "title" | "metaStrip";

export interface DetailPageProps {
  title: ReactNode;
  refPill?: ReactNode;
  headerVariant?: DetailPageHeaderVariant;
  metaStripItems?: ReadonlyArray<FieldListItem>;
  /** Extra slot rendered alongside metaStripItems (e.g. status badge). */
  metaStripExtra?: ReactNode;
  /** Single primary action (legacy). Use headerActions for clusters. */
  primaryAction?: ReactNode;
  /** Action cluster rendered top-right. Wins over primaryAction if both supplied. */
  headerActions?: ReactNode;
  banner?: ReactNode;
  /** Optional in Plan II. When omitted/empty, children render as the body. */
  tabs?: readonly DetailTab[];
  defaultTab?: string;
  /**
   * When provided, the tab selection is controlled externally. The component
   * will call `onTabChange` when the user clicks a tab trigger and will honour
   * `activeTabId` as the source of truth for the selected tab.
   * When omitted, the component manages tab state internally (uncontrolled),
   * which preserves backward compatibility for all other callers.
   */
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  footer?: ReactNode;
  /** Optional right pane (e.g. PDF preview). 500px wide, hidden below md breakpoint. */
  rightPane?: ReactNode;
  backTo?: string;
  /** Action cluster rendered on the far right of the tabs row */
  tabActions?: ReactNode;
  /** Global toolbar rendered inside the tab content block at the very top for all tabs */
  tabToolbar?: ReactNode;
  children?: ReactNode;
  /** Replaces the title / metaStrip header block entirely. When set,
   *  `title`, `refPill`, `metaStripItems`, `metaStripExtra`,
   *  `headerVariant`, and `headerActions` are all ignored. */
  headerOverride?: ReactNode;
}

export function DetailPage({
  title,
  refPill,
  headerVariant = "title",
  metaStripItems,
  metaStripExtra,
  primaryAction,
  headerActions,
  banner,
  tabs,
  defaultTab,
  activeTabId,
  onTabChange,
  footer,
  rightPane,
  backTo,
  tabActions,
  tabToolbar,
  children,
  headerOverride,
}: DetailPageProps) {
  const action = headerActions ?? primaryAction;
  const hasTabs = tabs && tabs.length > 0;
  // Controlled mode: when `activeTabId` is provided by the caller we defer to
  // it as the source of truth and propagate user clicks via `onTabChange`.
  // Uncontrolled mode (default): internal state, preserves backward compat.
  const [internalActive, setInternalActive] = useState(defaultTab ?? tabs?.[0]?.id ?? "");
  const active = activeTabId !== undefined ? activeTabId : internalActive;
  const setActive = (id: string) => {
    if (activeTabId !== undefined) {
      onTabChange?.(id);
    } else {
      setInternalActive(id);
    }
  };

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  const navigate = useNavigate();

  const backLink = backTo ? (
    <Link 
      to={backTo} 
      onClick={(e) => {
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
          if (window.history.state && window.history.state.idx > 0) {
            e.preventDefault();
            navigate(-1);
          }
        }
      }}
      className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
    >
      <ChevronLeft className="mr-1 h-4 w-4" />
      Back
    </Link>
  ) : null;

  return (
    <PageContainer banner={banner}>
      {backLink && portalNode
        ? createPortal(backLink, portalNode)
        : backLink && !portalNode && (
            <div className="mb-2 inline-block">
              {backLink}
            </div>
          )}

      {headerOverride ?? (
        headerVariant === "title" ? (
          <PageHeader title={title} refPill={refPill} primaryAction={action} className="mb-4" />
        ) : (
          <div className="mb-4 rounded-lg border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 items-center gap-4">
                {metaStripItems && metaStripItems.length > 0 && (
                  <FieldList
                    items={metaStripItems}
                    className="grid-cols-[max-content_1fr] md:grid-cols-[max-content_1fr_max-content_1fr_max-content_1fr_max-content_1fr]"
                  />
                )}
                {metaStripExtra}
              </div>
              {action && <div className="flex items-center gap-2">{action}</div>}
            </div>
          </div>
        )
      )}

      {rightPane ? (
        <div className="md:grid md:grid-cols-[1fr_500px] md:gap-6">
          <div className="min-w-0">
            {hasTabs ? (
              <Tabs value={active} onValueChange={setActive}>
                <div className="flex w-full items-end justify-between gap-4 overflow-hidden">
                  <TabsList className="flex flex-nowrap flex-1 min-w-0 justify-start overflow-x-auto mb-0 gap-0 p-0 bg-transparent">
                    {tabs.map((t) => (
                      <TabsTrigger 
                        key={t.id} 
                        value={t.id} 
                        className="whitespace-nowrap flex-shrink-0 rounded-t-xl rounded-b-none px-6 py-1.5 text-sm font-medium data-[state=active]:font-bold bg-info/15 text-primary hover:bg-info/25 data-[state=active]:bg-[#0f3453] data-[state=active]:text-white shadow-none data-[state=active]:shadow-none border-r border-blue-200 last:border-r-0 data-[state=active]:border-r-[#0f3453]"
                      >
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {tabActions && <div className="flex items-center gap-2 mb-1.5">{tabActions}</div>}
                </div>
                {tabs.map((t) => (
                  <TabsContent
                    key={t.id}
                    value={t.id}
                    {...(t.forceMount ? { forceMount: true, hidden: active !== t.id } : {})}
                    className="bg-slate-100 border border-slate-200 rounded-b-2xl rounded-tr-2xl overflow-hidden mt-0"
                  >
                    {tabToolbar && (
                      <div className="bg-[#e5e5e5] px-4 py-2 flex flex-wrap items-center gap-2 border-b border-slate-200">
                        {tabToolbar}
                      </div>
                    )}
                    <div className="p-4">
                      {t.content}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            ) : null}
            {children}
          </div>
          <aside className="hidden md:block">{rightPane}</aside>
        </div>
      ) : (
        <>
          {hasTabs ? (
            <Tabs value={active} onValueChange={setActive}>
              <div className="flex w-full items-end justify-between gap-4 overflow-hidden">
                <TabsList className="flex flex-nowrap flex-1 min-w-0 justify-start overflow-x-auto mb-0 gap-0 p-0 bg-transparent">
                  {tabs.map((t) => (
                    <TabsTrigger 
                      key={t.id} 
                      value={t.id} 
                      className="whitespace-nowrap flex-shrink-0 rounded-t-xl rounded-b-none px-6 py-1.5 text-sm font-medium data-[state=active]:font-bold bg-info/15 text-primary hover:bg-info/25 data-[state=active]:bg-[#0f3453] data-[state=active]:text-white shadow-none data-[state=active]:shadow-none border-r border-blue-200 last:border-r-0 data-[state=active]:border-r-[#0f3453]"
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {tabActions && <div className="flex items-center gap-2 mb-1.5">{tabActions}</div>}
              </div>
              {tabs.map((t) => (
                <TabsContent
                  key={t.id}
                  value={t.id}
                  {...(t.forceMount ? { forceMount: true, hidden: active !== t.id } : {})}
                  className="bg-white border border-slate-200 rounded-b-2xl rounded-tr-2xl overflow-hidden mt-0"
                >
                  {tabToolbar && (
                    <div className="bg-[#e5e5e5] px-4 py-2 flex flex-wrap items-center gap-2 border-b border-slate-200">
                      {tabToolbar}
                    </div>
                  )}
                  <div className="p-4">
                    {t.content}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          ) : null}
          {children}
        </>
      )}

      {footer && (
        <div className="mt-8 pb-8">
          {footer}
        </div>
      )}
    </PageContainer>
  );
}
